import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { api } from '../lib/api';

// Homepage "News & Insights" — real, published blog articles only. Shows
// nothing (just the section header + CTA) rather than fabricated posts
// when the blog is empty.
export default function NewsInsights() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    let active = true;
    api
      .listArticles()
      .then((data) => {
        if (active) setArticles((data.articles || []).slice(0, 3));
      })
      .catch(() => {
        if (active) setArticles([]);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <Box component="section" sx={{ py: { xs: 7, md: 10 }, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          sx={{ mb: 4 }}
          spacing={2}
        >
          <Box>
            <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'text.secondary', mb: 1 }}>
              {t('home.caseStudies.eyebrow')}
            </Typography>
            <Typography component="h2" sx={{ fontFamily: 'var(--font-serif)', fontSize: { xs: '2rem', md: '2.6rem' }, lineHeight: 1.08, letterSpacing: '-0.02em' }}>
              {t('home.caseStudies.title')}
            </Typography>
          </Box>
          <Button component={RouterLink} to="/blog" variant="text" endIcon={<ArrowForwardRoundedIcon />} sx={{ color: 'text.primary' }}>
            {t('home.caseStudies.cta')}
          </Button>
        </Stack>

        {articles.length > 0 ? (
          <Grid container spacing={3}>
            {articles.map((post) => (
              <Grid key={post.id} item xs={12} md={4}>
                <Paper
                  component={RouterLink}
                  to={`/blog/${post.slug}`}
                  variant="outlined"
                  sx={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    p: { xs: 3, md: 3.5 },
                    borderRadius: 3,
                    height: '100%',
                    bgcolor: 'background.default',
                    transition: 'transform 200ms ease, border-color 200ms ease',
                    '&:hover': { transform: 'translateY(-4px)', borderColor: 'primary.main' }
                  }}
                >
                  <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'text.secondary', mb: 2 }}>
                    {post.category || 'Update'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.6rem', lineHeight: 1.15, letterSpacing: '-0.015em', mb: 1.5 }}>
                    {post.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>{post.summary}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Container>
    </Box>
  );
}
